<template>
  <!-- Resolve path first — no Try another method flash for FEATURE_DISABLED / ALLOW_UNENROLLED -->
  <div :class="$style.formContainer">
    <LoginCard
      v-if="!isAdmin() && !pathResolved"
      key="resolving"
      header="Reset User Password"
    >
      <div :class="$style.resolvingContainer">
        <EmailBadge :email="userEmail">
          <template slot="badgeAction">
            <button
              :class="$style.actionLink"
              type="button"
              data-test-id="badgeActionButton"
              @click="goToUserLogin"
            >
              Change
            </button>
          </template>
        </EmailBadge>
        <LoginActionButton
          :action="noop"
          :isLoading="true"
          type="button"
        >
          Continue
        </LoginActionButton>
      </div>
    </LoginCard>

    <transitionGroup
      v-else
      :enterClass="$style.fadeEnter"
      :enterActiveClass="$style.fadeEnterActive"
      :leaveActiveClass="$style.fadeLeaveActive"
      :leaveToClass="$style.fadeLeaveTo"
    >
      <UserResetPasswordRequest
        v-if="!isAdmin()"
        key="user"
        :errorMessage="errorMessage"
        :isLoading="isLoading"
        :success="success"
        @goToLogin="goToUserLogin"
        @selectMfa="onSelectMfa"
        @submit="request"
      />
      <AdminResetPasswordRequest
        v-else
        key="admin"
        :errorMessage="errorMessage"
        :isLoading="isLoading"
        :success="success"
        @goToLogin="goToAdminLogin"
        @submit="request"
      />
    </transitionGroup>
    <LogoIcon />
  </div>
</template>

<script>
import {
  clearPwdResetSessionEmail,
  syncPwdResetSessionEmail,
} from 'client/util/pwdResetSessionEmail';
import { mapStores } from 'pinia';
import AdminResetPasswordRequest from 'client/containers/admin/PasswordReset/AdminResetPasswordRequest.vue';
import EmailBadge from 'client/components/EmailBadge.vue';
import LoginActionButton from 'client/components/LoginActionButton.vue';
import LoginCard from 'client/components/LoginCard.vue';
import UserLoginStore from 'client/stores/UserLoginStore';
import UserResetPasswordRequest from 'client/containers/user/UserResetPassword/UserResetPasswordRequest.vue';
import api from 'client/api';
import loginUtil from 'client/components/mixins/loginUtil';
import routerService from 'client/services/routerService';
import userResetPasswordService from 'client/services/userResetPasswordService';

export default {
  name: 'RequestPasswordReset',

  components: {
    AdminResetPasswordRequest,
    EmailBadge,
    LoginActionButton,
    LoginCard,
    UserResetPasswordRequest,
  },

  mixins: [loginUtil],

  props: {
    clientType: {
      type: String,
      required: true,
    },
    context: {
      type: String,
      default: undefined,
    },
    redirectTo: {
      type: String,
      default: undefined,
    },
  },

  data: () => ({
    success: false,
    pathResolved: false,
  }),

  computed: {
    ...mapStores(UserLoginStore),

    userEmail() {
      return this.userLoginStore.email || '';
    },
  },

  created() {
    // Admin forgot-password is unchanged (no MFA-direct)
    if (this.isAdmin()) {
      this.pathResolved = true;
    }
  },

  async mounted() {
    if (this.pathResolved) {
      return;
    }
    await this.resolveResetPath();
  },

  methods: {
    noop() {},

    async resolveResetPath() {
      const email = syncPwdResetSessionEmail(
        () => this.userLoginStore.email || '',
        (value) => { this.userLoginStore.email = value; },
      );
      if (!email) {
        this.userLoginStore.eligibility = null;
        this.pathResolved = true;
        return;
      }

      try {
        const result = await userResetPasswordService.checkMfaEligibility({ email });
        this.userLoginStore.eligibility = result;
      } catch {
        // Probe failure: email-only, do not treat as anti-enum (no reason)
        this.userLoginStore.eligibility = null;
      }

      this.pathResolved = true;
    },

    goToAdminLogin() {
      routerService.goToAdminLogin();
    },

    ssoQueryParams() {
      return this.context && this.redirectTo
        ? { context: this.context, redirectTo: this.redirectTo }
        : {};
    },

    goToUserLogin() {
      clearPwdResetSessionEmail();
      routerService.goToUserLogin({ query: this.ssoQueryParams() });
    },

    onSelectMfa() {
      this.userLoginStore.pwdResetToken = '';
      this.userLoginStore.pwdResetMfaVerified = false;
      this.userLoginStore.resetPath = 'mfa-direct';
      routerService.goToUserMfaDirectPasswordReset({ query: this.ssoQueryParams() });
    },

    request(email) {
      this.isLoading = true;
      this.errorMessage = '';
      api[this.clientType].requestPasswordReset({ email })
        .then(() => {
          this.isLoading = false;
          this.success = true;
        }).catch(() => {
          this.isLoading = false;
          this.errorMessage = 'There was an error handling your request.';
        });
    },
  },
};
</script>

<style module>
@import '../css/login-components.css';
@value (jcInformational900) from 'css/brand-colors.css';

.resolvingContainer {
  padding: 0 1rem;
}

.actionLink {
  background-color: transparent;
  border: none;
  color: jcInformational900;
  cursor: pointer;
  margin-left: 1rem;
  padding: 0;
  text-decoration: none;
}

.fadeEnterActive {
  transition: opacity 0.35s 0.35s;
}

.fadeLeaveActive {
  transition: opacity 0.35s;
}

.fadeEnter,
.fadeLeaveTo {
  opacity: 0;
}
</style>
